import { useMemo, useState } from 'react';
import React from 'react';
import { Card, Icon, IconButton, Tooltip } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { getState } from '../../../store';
import useFirestoreCollection from '../../../@types/hooks/firestore/useFirestoreCollection';
import ValueAddedTaxModelHelper from '../../../@types/model/masterData/valueAddedTax/valueAddedTaxModelHelper';
import GeneralFunctions from '../../../store/general/functions';
import MaterialTable from '../../customComponents/materialTable/Table';
import ConfirmDialog from '../../customComponents/dialog/ConfirmDialog';
import { IValueAddedTax } from '../../../@types/model/masterData/valueAddedTax/valueAddedTax';
import ValueAddedTaxEditDialog from './components/ValueAddedTaxEditDialog';
import lodash from 'lodash';
import moment from 'moment';

const ValueAddedTaxManager = () : React.ReactElement => {
    const currentUser = getState().auth.session;
    const [selectedValueAddedTax, setSelectedValueAddedTax] = useState<IValueAddedTax | null>(null);
    const [valueAddedTaxToDelete, setValueAddedTaxToDelete] = useState<IValueAddedTax | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);

    const { data: valueAddedTaxes, isLoading: isLoading } = useFirestoreCollection<IValueAddedTax>(ValueAddedTaxModelHelper.collection());

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const handleOpenModal = () : void => {
        setIsEditDialogOpen(true);
    };

    const handleOnDismiss = () : void => {
        setSelectedValueAddedTax(null);
        setIsEditDialogOpen(false);
    };

    const editValueAddedTax = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) : void => {
        const row = valueAddedTaxes.find(x => x.guid === event.currentTarget.value);

        if (!row) return;

        setSelectedValueAddedTax(row);
        setIsEditDialogOpen(true);
    };

    const deleteValueAddedTax = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) : void => {
        const row = valueAddedTaxes.find(x => x.guid === event.currentTarget.value);

        if (!row) return;

        setValueAddedTaxToDelete(row);
    };

    const handleValueAddedTaxDelete = async () : Promise<void> => {
        if (!currentUser || !valueAddedTaxToDelete) return;

        const valueAddedTax = ValueAddedTaxModelHelper.createInactiveSave(currentUser, valueAddedTaxToDelete);

        try {
            await ValueAddedTaxModelHelper.save(valueAddedTax);
            GeneralFunctions.generalShowSuccessSnackbar('Successfully inactivated Value Added Tax.');
        } catch (e) {
            GeneralFunctions.generalShowErrorSnackbar('An error occurred while inactivating Value Added Tax.');
        }

        setValueAddedTaxToDelete(null);
    };

    /*================================================================================================================
     *                                                      Memos
     * ==============================================================================================================*/

    const sortedRows = useMemo<Array<IValueAddedTax>>(() => {
        return lodash.sortBy(valueAddedTaxes, 
            [
                x => !x.isActive,
                x => x.startDate,
            ]
        );
    }, [valueAddedTaxes]);

    const lastActiveRow = useMemo<IValueAddedTax | undefined>(() => {
        return lodash.chain(sortedRows)
            .filter(x => x.isActive)
            .last()
            .value();
    }, [sortedRows]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div className={'fdc flx1 p10 mh0 mw0 bcg0'}>
            <Card className={'flx1 fdc'}>
                <MaterialTable<IValueAddedTax>
                    id='ValueAddedTaxTable'
                    data={sortedRows}
                    isLoading={isLoading}
                    rowsPerPage={100}
                    columns={[
                        {
                            header: '',
                            field: '',
                            width: 100,
                            enableSort: true,
                            enableFilter: true,
                            paddingRight: 4,
                            fixed: true,
                            renderCell: row => (
                                <div className='aic'>
                                    <Tooltip title='Edit'>
                                        <div>
                                            <IconButton color='primary' value={row.guid} onClick={editValueAddedTax} disabled={row.guid !== lastActiveRow?.guid || !row.isActive}>
                                                <EditIcon />
                                            </IconButton>
                                        </div>
                                    </Tooltip>
                                    <Tooltip title='Delete'>
                                        <div>
                                            <IconButton color='primary' disabled={row.guid !== lastActiveRow?.guid || !row.isActive} 
                                                value={row.guid} 
                                                onClick={deleteValueAddedTax} 
                                                className={row.guid !== lastActiveRow?.guid || !row.isActive ? '' : 'cr'}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </div>
                                    </Tooltip>
                                </div>
                            ),
                        }, 
                        {
                            header: 'Value',
                            field: 'value',
                            width: 140,
                            enableSort: true,
                            renderCell: n => `${n.value}%`,
                        }, 
                        {
                            header: 'Start Date',
                            field: 'startDate',
                            width: 140,
                            enableSort: true,
                            renderCell: n => n.startDate ? moment.utc(n.startDate.toMillis()).format('YYYY-MM-DD') : '',
                        }, 
                        {
                            header: 'End Date',
                            field: 'endDate',
                            width: 140,
                            enableSort: true,
                            renderCell: n => n.endDate ? moment.utc(n.endDate.toMillis()).format('YYYY-MM-DD') : '',
                        }, 
                        {
                            header: 'Created By',
                            field: 'createdByName',
                            width: 140,
                            enableSort: true,
                        },   
                        {
                            header: 'Updated By',
                            field: 'updatedByName',
                            width: 140,
                            enableSort: true,
                        },  
                        {
                            header: 'Is Active',
                            field: 'isActive',
                            width: 215,
                            enableSort: true,
                            renderCell: n => !n.isActive ? (<Icon className='cr'>close</Icon>) : (<Icon className='cdg'>check</Icon>),
                        }]}
                />
            </Card>
            <div className={'fdrr mt15 mr15'}>
                {
                    currentUser?.employee.IsValueAddedTaxAdmin && 
                        !isLoading &&
                        <Tooltip title='Create'>
                            <div>
                                <IconButton onClick={handleOpenModal} className={'bcy'}>
                                    <Icon className={'cp'}>
                                        add
                                    </Icon>
                                </IconButton>
                            </div>
                        </Tooltip>
                }
            </div>
            {
                isEditDialogOpen && 
                    <ValueAddedTaxEditDialog 
                        isOpen={isEditDialogOpen} 
                        currentUser={currentUser}
                        selectedValueAddedTax={selectedValueAddedTax} 
                        onDismiss={handleOnDismiss}
                        sortedRows={sortedRows}
                        valueAddedTaxes={valueAddedTaxes}
                    />
            }
            {
                !!valueAddedTaxToDelete &&
                    <ConfirmDialog 
                        open={!!valueAddedTaxToDelete}
                        title={'Delete Value Added Tax'}
                        message={'Are you sure you want to inactivate the selected entry?'}
                        onAccept={handleValueAddedTaxDelete}
                        onClose={() => setValueAddedTaxToDelete(null)}
                    />
            }
        </div>
    );
};

export default ValueAddedTaxManager;